import { toast } from "sonner";
import { powtoonBE } from "@/lib/powtoon-be/client";
import { liveavatar } from "@/lib/liveavatar/client";
import { buildContextPrompt } from "@/lib/prompts";
import type {
  CreateScenarioInput,
  Scenario,
  ScenarioSourceItem,
  ScenarioContextBlock,
  MCQConfig,
  ProviderId,
  RolePlayConfig,
  TavusPalBlock,
  DidAgentBlock,
  KalturaAgentBlock,
} from "@/lib/powtoon-be/types";
import type { SourceInputDraft } from "@/lib/scenarios/sourceDraft";
import { draftToPersisted } from "@/lib/scenarios/sourceDraft";
import type { DisplayFormValue } from "@/components/scenarios/LearnerDisplayPicker";
import { syncTavusPal } from "@/lib/scenarios/providers/tavus/savePal";
import {
  reconcileSavedDocuments,
  deleteTavusDocuments,
} from "@/lib/scenarios/providers/tavus/knowledgeDocuments";
import { syncDidAgent } from "@/lib/scenarios/providers/did/saveAgent";
import { did as didClient } from "@/lib/scenarios/providers/did/client";
import {
  reconcileSavedDocuments as reconcileDidDocuments,
  deleteDidDocuments,
} from "@/lib/scenarios/providers/did/knowledgeDocuments";
import {
  reconcilePresentationTool,
  deletePresentationTool,
  didPresentationInstruction,
} from "@/lib/scenarios/providers/did/presentationTool";
import { syncKalturaAgent } from "@/lib/scenarios/providers/kaltura/saveAgent";
import { kalturaCapabilities } from "@/lib/scenarios/providers/kaltura/capabilities";
import { reconcileKalturaPresentationTools } from "@/lib/scenarios/providers/kaltura/presentationTool";
import { composeKalturaDirective } from "@/lib/scenarios/providers/kaltura/directive";

import { documentCandidates } from "@/lib/scenarios/providers/sourceKeys";
import { resolveDisplayForPersist, isPdfDisplaySource } from "@/lib/scenarios/display";
import { isRolePlayConfigEmpty } from "@/lib/scenarios/modes/role_play/schema";
import { PROVIDER_SPECS, interactionModeOf } from "@/lib/scenarios/providers/spec";
import type { ScenarioDisplaySource } from "@/lib/powtoon-be/types";


type SaveScenarioMode = { kind: "create" } | { kind: "update"; scenarioId: string };
export type SaveScenarioMeta = {
  avatarPreviewUrl?: string;
  avatarChanged: boolean;
  /** Scene background flattened behind the avatar in the stored thumbnail. */
  backgroundPreviewUrl?: string;
  backgroundChanged?: boolean;
  sourceDrafts?: SourceInputDraft[];
  initialInputs?: ScenarioSourceItem[];
  /** Current MCQ form value, or null when interaction mode is not MCQ. */
  questionnaire?: MCQConfig | null;
  /** Value loaded into the form — used for dirty check to avoid empty versions. */
  initialQuestionnaire?: MCQConfig | null;
  /** Existing versioned IDs on the scenario, newest first. */
  initialQuestionnaireIds?: string[];
  /** Current role-play form value, or null when the mode is not role play. */
  roleplay?: RolePlayConfig | null;
  /** Value loaded into the form — used for the dirty check. */
  initialRoleplay?: RolePlayConfig | null;
  /** Existing `live_avatar_roleplay` artifact ids, newest first. */
  initialRoleplayIds?: string[];
  /** Learner-display draft. Save step resolves draftId → object_id after uploads. */
  display?: DisplayFormValue | null;
  /**
   * Which backend runs this scenario. Determines the sync flow:
   * `liveavatar` → LiveAvatar context sync + questionnaire artifact,
   * `tavus` → Tavus pal sync, no context/questionnaire.
   * Unset defaults to `liveavatar` for backwards compatibility.
   */
  provider?: ProviderId;
  /** Existing Tavus pal id on the scenario (create → PATCH, absent → POST). */
  initialPalId?: string;
  /**
   * Voice id last persisted on the pal. Used by the Tavus save path to
   * decide whether to emit a voice op — sending one on every save was
   * what triggered the PATCH 500 on pals that never had a voice override.
   */
  initialElevenLabsVoiceId?: string;
  /** Existing D-ID agent id on the scenario (create → PATCH, absent → POST). */
  initialAgentId?: string;
  /**
   * Tavus-only: `source_key` of the scenario source the trainer attached as
   * the knowledge-base document, or undefined for "None". See
   * `providers/tavus/knowledgeDocuments.ts`.
   */
  knowledgeDocumentSourceKey?: string;
  /**
   * When duplicating a scenario, clone these uploaded attachments from the
   * source scenario into the newly-created one. Object ids in `sourceDrafts`
   * that match `objectIds` are rewritten to the freshly uploaded ids before
   * persistence so the duplicate is fully independent of the source.
   */
  attachmentsToClone?: { sourceScenarioId: string; objectIds: string[] };
};

function questionnaireChanged(
  next: MCQConfig | null | undefined,
  prev: MCQConfig | null | undefined,
): boolean {
  if (!next) return false;
  // Brand-new questionnaire with no questions yet — don't persist an empty artifact.
  if (!prev) return next.questions.length > 0;
  return JSON.stringify(prev) !== JSON.stringify(next);
}

function roleplayChanged(
  next: RolePlayConfig | null | undefined,
  prev: RolePlayConfig | null | undefined,
): boolean {
  if (!next) return false;
  if (!prev) return !isRolePlayConfigEmpty(next);
  return JSON.stringify(prev) !== JSON.stringify(next);
}

/**
 * Publish gate for role play: a shared (non-hidden) scenario must carry a
 * goal, at least one role-play scenario and at least one competency metric,
 * otherwise the Powtoon BE scoring job has nothing to grade against.
 */
function validateRoleplayForPublish(rp: RolePlayConfig | null | undefined): string | null {
  if (!rp || isRolePlayConfigEmpty(rp)) {
    return "Fill in the role play before publishing. Drafts can be saved empty by setting visibility to Hidden.";
  }
  if (!rp.goal.trim()) {
    return "Add the overall role-play goal before publishing this scenario.";
  }
  const incomplete = rp.scenarios.some(
    (s) =>
      !s.avatar_role.trim() ||
      !s.learner_role.trim() ||
      s.assessment.competency_metrics.filter((m) => m.name.trim().length > 0).length === 0,
  );
  if (incomplete) {
    return "The role play needs the avatar role, the learner role and at least one assessment metric before this scenario can be published.";
  }
  return null;
}

/**
 * Validate the questionnaire form value before persisting. Returns a
 * business-facing error message when the questionnaire isn't ready for a
 * non-draft (published / link-shared) scenario, or null when it's OK.
 */
function validateQuestionnaireForPublish(q: MCQConfig | null | undefined): string | null {
  if (!q || q.questions.length === 0) {
    return "Add at least one question before publishing this scenario. Drafts can be saved empty by setting visibility to Hidden.";
  }
  const incomplete = q.questions.some(
    (qu) =>
      !qu.question.trim() ||
      qu.answers.length === 0 ||
      !qu.answers.some((a) => a.text.trim().length > 0),
  );
  if (incomplete) {
    return "Each question needs a title and at least one answer before this scenario can be published.";
  }
  return null;
}

/**
 * Download each source attachment and re-upload it under the new scenario.
 * Returns a map of old object_id → new object_id.
 */
async function cloneAttachments(
  newScenarioId: string,
  sourceScenarioId: string,
  objectIds: string[],
): Promise<Map<string, string>> {
  const remap = new Map<string, string>();
  for (const oldId of objectIds) {
    const att = await powtoonBE.attachments.get(sourceScenarioId, oldId);
    const url = att.file || att.file_url;
    if (!url) throw new Error(`Attachment ${oldId} has no downloadable URL`);
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to download attachment ${oldId} (${res.status})`);
    const blob = await res.blob();
    const file = new File([blob], att.name || "file", { type: blob.type });
    const uploaded = await powtoonBE.attachments.upload(newScenarioId, file, att.name);
    remap.set(oldId, uploaded.id);
  }
  return remap;
}

function remapUploadDrafts(
  drafts: SourceInputDraft[],
  remap: Map<string, string>,
): SourceInputDraft[] {
  if (remap.size === 0) return drafts;
  return drafts.map((d) => {
    if (d.kind === "upload" && d.object_id && remap.has(d.object_id)) {
      return { ...d, object_id: remap.get(d.object_id)! };
    }
    return d;
  });
}

async function uploadAndBuildInputs(
  scenarioId: string,
  drafts: SourceInputDraft[],
  initialInputs: ScenarioSourceItem[],
): Promise<{ inputs: ScenarioSourceItem[]; drafts: SourceInputDraft[] }> {
  // 1. Upload any pending files.
  const finalDrafts: SourceInputDraft[] = [];
  for (const d of drafts) {
    if (d.kind === "upload" && d._file && !d.object_id) {
      const att = await powtoonBE.attachments.upload(scenarioId, d._file, d.file_name);
      finalDrafts.push({ ...d, object_id: att.id, _file: undefined });
    } else {
      finalDrafts.push(d);
    }
  }

  // 2. Remove attachments that were dropped from the list.
  const keptIds = new Set(
    finalDrafts
      .filter((d): d is Extract<SourceInputDraft, { kind: "upload" }> => d.kind === "upload")
      .map((d) => d.object_id)
      .filter((id): id is string => !!id),
  );
  for (const prev of initialInputs) {
    if (prev.kind === "upload" && prev.object_id && !keptIds.has(prev.object_id)) {
      try {
        await powtoonBE.attachments.remove(scenarioId, prev.object_id);
      } catch (err) {
        console.warn("Failed to remove orphan attachment", prev.object_id, err);
      }
    }
  }

  // 3. Map drafts to persisted shape.
  const inputs = finalDrafts
    .map(draftToPersisted)
    .filter((x): x is ScenarioSourceItem => x !== null);
  return { inputs, drafts: finalDrafts };
}

/**
 * Provider-specific tail of the save pipeline: given the provider's content
 * block (already carrying the post-upload `inputs` and the refreshed
 * `questionnaire_ids`), push it upstream and return whatever the provider
 * wrote back — as a patch on the block and/or on the top-level content.
 *
 * Everything before this point (uploads, attachment cloning, questionnaire
 * versioning, shell creation, persistence, thumbnail) is provider-agnostic and
 * lives in `saveScenarioWithContext`.
 */
type ProviderSyncStep = (ctx: {
  input: CreateScenarioInput;
  meta: SaveScenarioMeta;
  block: Record<string, unknown>;
  finalInputs: ScenarioSourceItem[];
  /** Present once the scenario row exists (always in update mode). */
  scenarioId?: string;
  /** Learner display source as it will be persisted (post-upload). */
  displaySource?: ScenarioDisplaySource | null;
}) => Promise<{
  block?: Record<string, unknown>;
  content?: Record<string, unknown>;
  afterPersist?: () => Promise<void>;
}>;

const PROVIDER_SYNC: Record<ProviderId, ProviderSyncStep> = {
  liveavatar: async ({ input, block }) => {
    const cb = block as ScenarioContextBlock;
    // Always re-sync the LiveAvatar context on every save. Rebuild the prompt
    // as a safety net if for any reason the form left it empty.
    const promptToSync =
      cb.generated_prompt && cb.generated_prompt.trim().length > 0
        ? cb.generated_prompt
        : buildContextPrompt({
            mode: cb.interaction_mode,
            persona: cb.persona,
            knowledge: cb.knowledge,
            instructions: cb.instructions,
          });
    try {
      let contextId = cb.liveavatar_context_id;
      if (contextId) {
        await liveavatar.updateContext({
          context_id: contextId,
          name: input.name,
          prompt: promptToSync,
          opening_text: cb.opening_text ?? "",
        });
      } else {
        const res = await liveavatar.createContext({
          context_name: input.name ?? "",
          context_prompt: promptToSync,
          opening_text: cb.opening_text ?? "",
        });
        contextId = res.context_id;
      }
      return {
        block: { generated_prompt: promptToSync, liveavatar_context_id: contextId },
        // `context_id` mirrors the block value for the runtime player.
        content: { context_id: contextId },
      };
    } catch (err) {
      const msg = (err as Error)?.message || "Could not sync persona to LiveAvatar";
      toast.error(`Save failed: ${msg}`);
      throw err;
    }
  },
  tavus: async ({ input, meta, block, finalInputs }) => {
    const pal = block as TavusPalBlock;
    // Documents are registered in the editor before save; here we only keep
    // the pairings whose source still exists and retire the rest.
    const docs = reconcileSavedDocuments({
      entries: pal.knowledge_documents ?? [],
      selectedSourceKey: meta.knowledgeDocumentSourceKey,
      inputs: finalInputs,
    });
    return {
      block: await syncTavusPal({
        scenarioName: input.name,
        face_id: pal.face_id ?? "",
        elevenlabs_voice_id: pal.elevenlabs_voice_id,
        system_prompt: pal.system_prompt,
        opening_text: pal.opening_text,
        inputs: finalInputs,
        existingPalId: meta.initialPalId || pal.pal_id,
        previousElevenLabsVoiceId: meta.initialElevenLabsVoiceId,
        knowledgeDocuments: docs.knowledge_documents,
        knowledgeDocumentId: docs.knowledge_document_id,
        presentation: pal.presentation,
        internetSearch: pal.internet_search,
      }),
      afterPersist: docs.retiredDocumentIds.length
        ? async () => {
            await deleteTavusDocuments(docs.retiredDocumentIds);
          }
        : undefined,
    };
  },
  did: async ({ input, meta, block, finalInputs, displaySource }) => {
    const did = block as DidAgentBlock;
    // Documents are registered in the editor; here we only keep the pairings
    // whose source still exists and retire the rest.
    const docs = reconcileDidDocuments({
      entries: did.knowledge_documents ?? [],
      inputs: finalInputs,
    });
    // Knowledge base is only attached to D-ID agents in conversational mode.
    // In other modes, the knowledge text stays in the prompt and the document
    // base is left detached (but still owned by the scenario). The mode lives
    // on the provider block for D-ID — there is no top-level
    // `content.interaction_mode`.
    const isConversational = interactionModeOf(input.content) === "conversational";
    const knowledgeId = isConversational && docs.attach ? did.knowledge_id : undefined;
    // Presentation is a client tool on the agent: reconcile it first so the
    // agent write below carries the final tool list. The editor already owns
    // the gating — the toggle only renders in conversational mode with a
    // display source, and the adapter persists `present_document` gated on
    // conversational mode — so the save path trusts the block instead of
    // re-deriving the same conditions from a field D-ID never writes.
    const presentEnabled = !!did.present_document;
    // Paging tools only exist for a PDF — that is the one display kind our own
    // viewer controls (everything else is an iframe we cannot drive).
    const pageable = isPdfDisplaySource(displaySource);
    const presentation = await reconcilePresentationTool({
      enabled: presentEnabled,
      existingToolIds:
        did.presentation_tool_ids ??
        [did.presentation_tool_id, did.close_tool_id].filter((v): v is string => !!v),
      pageable,
      scenarioName: input.name,
    });
    // The tool description alone does not reliably drive the call — tell the
    // model the document starts hidden and which tools move it. The hint is
    // sent to D-ID only, never persisted into the trainer's instructions.
    const agentInstructions = presentEnabled
      ? [did.instructions?.trim(), didPresentationInstruction(pageable)].filter(Boolean).join("\n\n")
      : did.instructions;
    const synced = await syncDidAgent({
      scenarioName: input.name,
      presenter_id: did.presenter_id ?? "",
      presenter_type: did.presenter_type,
      voice_id: did.voice_id,
      background_id: did.background_id,
      background_url: did.background_url,
      // Locale only — the provider and the human-readable language are
      // looked up from the live D-ID voice catalog server-side.
      voice_locale: input.content.language,
      system_prompt: did.system_prompt,
      persona: did.persona,
      knowledge: did.knowledge,
      instructions: agentInstructions,
      opening_text: did.opening_text,
      inputs: finalInputs,
      existingAgentId: meta.initialAgentId || did.agent_id,
      knowledge_id: knowledgeId,
      knowledge_grounded: isConversational && !!did.knowledge_grounded,
      tool_ids: presentation.toolIds,
    });

    return {
      block: {
        ...synced.block,
        present_document: presentEnabled,
        presentation_tool_ids: presentation.toolIds.length ? presentation.toolIds : undefined,
        // Legacy single-id fields, kept so older readers still resolve the pair.
        presentation_tool_id: presentation.toolIds[0],
        close_tool_id: presentation.toolIds[1],
        // The base id survives even when nothing is attached, so re-adding a
        // document doesn't mint a second knowledge base for the scenario.
        knowledge_id: did.knowledge_id,
        knowledge_documents: docs.knowledge_documents,
        knowledge_grounded: !!did.knowledge_grounded,
      },
      afterPersist:
        synced.retiredAgentId || docs.retiredDocumentIds.length || presentation.retiredToolIds.length
          ? async () => {
              if (docs.retiredDocumentIds.length && did.knowledge_id) {
                await deleteDidDocuments(did.knowledge_id, docs.retiredDocumentIds);
              }
              for (const retiredId of presentation.retiredToolIds) {
                await deletePresentationTool(retiredId);
              }
              if (synced.retiredAgentId) {
                await didClient.deleteAgent(synced.retiredAgentId as string);
              }
            }
          : undefined,
    };
  },
  kaltura: async ({ input, meta, block, finalInputs, displaySource }) => {
    const kaltura = block as KalturaAgentBlock;
    // Knowledge reconcile: the editor syncs the record itself, so here we
    // only decide attach vs detach — entries whose source was removed stop
    // counting, and with nothing usable left the intellect detaches so the
    // prompt knowledge takes over again (same rule as D-ID).
    const liveKeys = new Set(documentCandidates(finalInputs).map((c) => c.key));
    const syncedKeys = (kaltura.knowledge_source_keys ?? []).filter((k) => liveKeys.has(k));
    // Single owner of the upstream capability + attach state (mode gating
    // included) — the session runtime derives it from the same function.
    const capabilities = kalturaCapabilities({
      block: kaltura,
      mode: interactionModeOf(block),
      syncedSourceCount: syncedKeys.length,
    });
    // Presentation is a set of browser client tools, same contract as D-ID —
    // except Kaltura's tool namespace is partner-level, so the four tools are
    // created once for the partner and every scenario just attaches their ids.
    // Paging tools only apply to a PDF — the one display kind our viewer
    // drives (everything else is an iframe we can't control).
    const pageable = isPdfDisplaySource(displaySource);
    const presentation = reconcileKalturaPresentationTools({
      enabled: capabilities.present_document,
      pageable,
    });

    // Capability descriptions alone don't reliably drive behaviour — tell the
    // brain the document starts hidden, and that a shared screen arrives as
    // periodic stills. Appended for Kaltura only, never persisted into the
    // trainer's prompt. Shared with the session-start reconcile so neither path
    // can write a directive the other would strip.
    const systemPrompt = composeKalturaDirective({
      system_prompt: kaltura.system_prompt,
      present_document: capabilities.present_document,
      pageable,
      screen_share: capabilities.screen_share,
    });
    const synced = await syncKalturaAgent({
      scenarioName: input.name,
      visual_id: kaltura.visual_id ?? "",
      voice_id: kaltura.voice_id ?? "",
      existingAvatarId: kaltura.avatar_id,
      system_prompt: systemPrompt,
      opening_text: kaltura.opening_text,
      language: kaltura.language,
      inputs: finalInputs,
      existingAgentId: meta.initialAgentId || kaltura.agent_id,
      existingIntellectId: kaltura.intellect_id,
      capabilities,
      presentationToolIds: presentation.toolIds,
    });
    return {
      block: {
        ...synced,
        // Keep the trainer's own prompt on the record — the presentation hint
        // is an upstream-only addition.
        system_prompt: kaltura.system_prompt,
        knowledge_id: kaltura.knowledge_id,
        knowledge_source_keys: syncedKeys,
        knowledge_sources: (kaltura.knowledge_sources ?? []).filter((s) => liveKeys.has(s.source_key)),
        knowledge_synced_at: kaltura.knowledge_synced_at,
        // The trainer's choice survives a mode round-trip; only the upstream
        // capability is gated by mode.
        web_search_enabled: kaltura.web_search_enabled === true,
        screen_share_enabled: kaltura.screen_share_enabled === true,
        present_document: kaltura.present_document === true,
      },
    };

  },
};


/**
 * Single save pipeline for every provider. The only provider-specific parts
 * are the content block location (from the provider spec registry) and the
 * upstream sync step (`PROVIDER_SYNC`).
 */
export async function saveScenarioWithContext(
  input: CreateScenarioInput,
  meta: SaveScenarioMeta,
  mode: SaveScenarioMode,
): Promise<Scenario> {
  const provider: ProviderId = meta.provider ?? input.content.provider ?? "liveavatar";
  const blockKey = PROVIDER_SPECS[provider].blockKey;

  let drafts = meta.sourceDrafts ?? [];
  const initialInputs = meta.initialInputs ?? [];
  const hasPendingUploads = drafts.some((d) => d.kind === "upload" && d._file && !d.object_id);
  const cloneList = meta.attachmentsToClone;
  const hasAttachmentsToClone = !!cloneList && cloneList.objectIds.length > 0;
  // Force a fresh questionnaire row when duplicating: create mode with a
  // loaded questionnaire and no existing versioned IDs on the new scenario.
  const forceQuestionnaireWrite =
    mode.kind === "create" &&
    !!meta.questionnaire &&
    !!meta.initialQuestionnaire &&
    (meta.initialQuestionnaireIds?.length ?? 0) === 0;
  let needsQuestionnaireWrite =
    forceQuestionnaireWrite ||
    questionnaireChanged(
      meta.questionnaire ?? null,
      meta.initialQuestionnaire ?? null,
    );
  // Same versioning contract for the role-play artifact.
  const forceRoleplayWrite =
    mode.kind === "create" &&
    !!meta.roleplay &&
    !!meta.initialRoleplay &&
    (meta.initialRoleplayIds?.length ?? 0) === 0;
  let needsRoleplayWrite =
    forceRoleplayWrite ||
    roleplayChanged(meta.roleplay ?? null, meta.initialRoleplay ?? null);

  // Business-rule gate: only published (non-hidden) questionnaire scenarios
  // require a complete questionnaire. Hidden scenarios are drafts and may
  // be saved with an empty / incomplete questionnaire.
  const isQuestionnaireMode = interactionModeOf(input.content) === "multiple_choice";
  const isRolePlayMode = interactionModeOf(input.content) === "role_play";
  const isHidden = input.content.publish_mode === "hidden";
  if (isQuestionnaireMode && !isHidden) {
    const err = validateQuestionnaireForPublish(meta.questionnaire ?? null);
    if (err) {
      toast.error(err);
      throw new Error(err);
    }
  }
  // For hidden drafts, never persist an empty questionnaire artifact.
  if (
    needsQuestionnaireWrite &&
    (!meta.questionnaire || meta.questionnaire.questions.length === 0)
  ) {
    needsQuestionnaireWrite = false;
  }
  if (isRolePlayMode && !isHidden) {
    const err = validateRoleplayForPublish(meta.roleplay ?? null);
    if (err) {
      toast.error(err);
      throw new Error(err);
    }
  }
  // Never persist an empty role-play artifact for a hidden draft.
  if (needsRoleplayWrite && isRolePlayConfigEmpty(meta.roleplay)) {
    needsRoleplayWrite = false;
  }

  // Ensure we have a scenarioId before uploading attachments.
  let scenarioId: string | undefined =
    mode.kind === "update" ? mode.scenarioId : undefined;
  let createdShell: Scenario | undefined;
  if (
    mode.kind === "create" &&
    (hasPendingUploads || needsQuestionnaireWrite || needsRoleplayWrite || hasAttachmentsToClone)
  ) {
    createdShell = await powtoonBE.scenarios.create({
      name: input.name,
      description: input.description,
      project_id: input.project_id,
      thumb_url: input.thumb_url,
      content: {},
    });
    scenarioId = createdShell.id;
  }

  // Clone source attachments into the new scenario and remap drafts so
  // their `object_id` references point to the freshly uploaded copies.
  if (scenarioId && hasAttachmentsToClone && cloneList) {
    try {
      const remap = await cloneAttachments(
        scenarioId,
        cloneList.sourceScenarioId,
        cloneList.objectIds,
      );
      drafts = remapUploadDrafts(drafts, remap);
    } catch (err) {
      const msg = (err as Error)?.message || "Could not copy attachments";
      toast.error(`Save failed: ${msg}`);
      throw err;
    }
  }

  // Upload files and rebuild persisted inputs[].
  let finalInputs: ScenarioSourceItem[] = initialInputs;
  let finalDrafts: SourceInputDraft[] = drafts;
  if (scenarioId) {
    const r = await uploadAndBuildInputs(scenarioId, drafts, initialInputs);
    finalInputs = r.inputs;
    finalDrafts = r.drafts;
  } else {
    // Pure create with no uploads: drafts are url/text only — map directly.
    finalInputs = drafts
      .map(draftToPersisted)
      .filter((x): x is ScenarioSourceItem => x !== null);
  }

  const incomingBlock = input.content[blockKey] as Record<string, unknown> | undefined;
  let nextBlock: Record<string, unknown> | undefined = incomingBlock
    ? { ...incomingBlock, inputs: finalInputs }
    : undefined;

  // Persist a new questionnaire version when the MCQ form value changed.
  let nextQuestionnaireIds = meta.initialQuestionnaireIds
    ? [...meta.initialQuestionnaireIds]
    : [];
  if (needsQuestionnaireWrite && scenarioId && meta.questionnaire) {
    try {
      const created = await powtoonBE.questionnaires.create(
        scenarioId,
        meta.questionnaire,
        input.name ? `${input.name} — questionnaire` : "Questionnaire",
      );
      nextQuestionnaireIds = [created.id, ...nextQuestionnaireIds];
    } catch (err) {
      const msg = (err as Error)?.message || "Could not save questionnaire";
      toast.error(`Save failed: ${msg}`);
      throw err;
    }
  }
  if (nextBlock) {
    nextBlock = {
      ...nextBlock,
      questionnaire_ids: nextQuestionnaireIds.length ? nextQuestionnaireIds : undefined,
    };
  }

  // Persist a new role-play artifact version when the form value changed.
  let nextRoleplayIds = meta.initialRoleplayIds ? [...meta.initialRoleplayIds] : [];
  if (needsRoleplayWrite && scenarioId && meta.roleplay) {
    try {
      const created = await powtoonBE.roleplays.create(
        scenarioId,
        meta.roleplay,
        input.name ? `${input.name} — role play` : "Role play",
      );
      nextRoleplayIds = [created.id, ...nextRoleplayIds];
    } catch (err) {
      const msg = (err as Error)?.message || "Could not save the role play";
      toast.error(`Save failed: ${msg}`);
      throw err;
    }
  }
  if (nextBlock) {
    nextBlock = {
      ...nextBlock,
      roleplay_ids: nextRoleplayIds.length ? nextRoleplayIds : undefined,
      // The played (and scored) inner scenario id — the runtime reads this
      // instead of an inline copy of the role-play content.
      roleplay_scenario_id:
        isRolePlayMode && meta.roleplay
          ? meta.roleplay.scenarios[0]?.id
          : undefined,
    };
  }

  // Resolved before the provider sync: D-ID needs to know whether the document
  // it may present is a PDF (pageable) to decide which client tools to attach.
  const persistedDisplay = resolveDisplayForPersist(
    meta.display ?? null,
    finalDrafts,
    finalInputs,
  );

  // Push the block upstream (LiveAvatar context / Tavus pal / D-ID agent /
  // Kaltura avatar+agent) and merge whatever the provider wrote back.
  let contentPatch: Record<string, unknown> = {};
  let afterPersist: (() => Promise<void>) | undefined;
  if (nextBlock) {
    const synced = await PROVIDER_SYNC[provider]({
      input,
      meta,
      block: nextBlock,
      finalInputs,
      scenarioId,
      displaySource: persistedDisplay?.source ?? null,
    });
    nextBlock = { ...nextBlock, ...(synced.block ?? {}) };
    contentPatch = synced.content ?? {};
    afterPersist = synced.afterPersist;
  } else if (provider !== "liveavatar") {
    // Only LiveAvatar tolerates a missing block (legacy scenarios without a
    // context block); the other providers cannot be synced without one.
    const err = `${provider} scenario is missing its \`${blockKey}\` block.`;
    toast.error(err);
    throw new Error(err);
  }

  const payload = {
    ...input,
    content: {
      ...input.content,
      ...contentPatch,
      [blockKey]: nextBlock,
      display: persistedDisplay,
    },
  };

  // If we created a shell earlier, finish with an update on that id.
  const scenario =
    mode.kind === "create"
      ? createdShell
        ? await powtoonBE.scenarios.update(createdShell.id, payload)
        : await powtoonBE.scenarios.create(payload)
      : await powtoonBE.scenarios.update(mode.scenarioId, payload);

  if (afterPersist) {
    try {
      await afterPersist();
    } catch (err) {
      console.warn("Scenario saved, but the replaced provider agent could not be removed", err);
    }
  }

  // Refresh the stored thumbnail when the avatar or the scene background
  // changed; the thumb is a flattened avatar-over-background image.
  if ((meta.avatarChanged || meta.backgroundChanged) && meta.avatarPreviewUrl) {
    try {
      await powtoonBE.scenarios.setThumbnailFromUrl(
        scenario.id,
        meta.avatarPreviewUrl,
        meta.backgroundPreviewUrl,
      );
    } catch (err) {
      toast.warning("Saved — thumbnail upload failed");
      console.error(err);
    }
  }

  return scenario;
}
